import { buildPrompt } from '@/ai/utils/promptBuilder'
import { examples } from '@/ai/prompts/common/examples'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { readModelNotes } from '@/ai/prompts/common/instructions'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import { createSystemMessage, createUserMessage, getRoleCommandAggregateBackground } from '@/ai/utils/promptUtils'
import { DomainModelRole } from '@/constants/cardTypes'

export const buildReadModelFromWorkflowPrompt = ({ eventId, workflow, cardTypes }) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  const event = workflow.domainEvents.find((e) => e.id === eventId) || {}
  const cardTypesMap = buildCardTypesMap(cardTypes)

  const commandCard = (event.cards || []).find((c) => {
    return cardTypesMap[c.cardTypeId]?.domainModelRole === DomainModelRole.COMMAND
  })
  const aggregateCard = (event.cards || []).find((c) => {
    return cardTypesMap[c.cardTypeId]?.domainModelRole === DomainModelRole.AGGREGATE_ROOT
  })

  const commandName = sanitizeCardDescription(commandCard?.description || '')
  const aggregateName = sanitizeCardDescription(aggregateCard?.description || '')
  const laneMap = new Map((workflow.lanes || []).map((l) => [l.id, l.name]))
  const roleName = laneMap.get(event.laneId) || ''

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  promptSections.push({
    title: 'Background Actor, Command, Aggregate',
    content: getRoleCommandAggregateBackground({
      currentStep: `"${event.description}"`,
      roleName,
      description: event.description,
      commandName,
      aggregateName
    })
  })

  const readModelRequestContent = commandName && aggregateName
    ? `Provide the Read Model as a set of attributes (or data fields) to be displayed to the actor BEFORE invoking the command "${commandName}" on the aggregate ${aggregateName}.`
    : `Provide the Read Model as a set of attributes (or data fields) to be displayed to the actor BEFORE invoking the command in domain event "${event.description}" (${roleName}).`

  promptSections.push({
    title: 'Request',
    content: `Generate a Read Model (or Query Model) for the actor (human or system) about to make the necessary decisions to take action and complete the domain event "${event.description}" (${roleName}). The purpose of the Read Model (or Query Model) is to define a query (for one or many items) that displays the current system state in a way that is relevant to the actor about to do the task at hand. ${readModelRequestContent} Usually, between 2 and 8 attributes or fields are sufficient, but it can be more or less. In most of the cases the generated data fields should match with the data fields of the entity and include the id field of the entity. An attribute can be a primitive value (e.g., status, totalAmount) or a reference ID to another entity (e.g., customerId). When displaying data from a related entity or value object, use a nested structure: a parent field named after the related entity in camelCase (e.g., cartItem, shippingAddress) with sub-fields for the attributes to display. An attribute or field name must be in camel case format (e.g., 'productName' instead of 'Product Name'). An attribute or field name must be at most 30 characters long. Also determine the cardinality: use "one-to-one" if the query returns a single item (e.g., viewing details of a specific order), or "one-to-many" if it returns a list of items (e.g., listing available products). Default to "one-to-many" if unclear.`
  })

  const readModelExample = {
    readModel: examples.readModelExampleCart,
    cardinality: 'one-to-one',
    readModelDescription: 'Returns the customer\'s current cart so they can review items before checkout.'
  }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonOutputFormatting('readModel', JSON.stringify(readModelExample))
  })

  promptSections.push({
    title: 'Note',
    contentType: 'bullet',
    content: [
      readModelNotes[0],
      readModelNotes[1],
      readModelNotes[7],
      readModelNotes[8],
      readModelNotes[9],
      readModelNotes[10]
    ]
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
